import React, { useMemo } from 'react';
import { orderSuccess as TEXT } from 'locales/en';
import { useMediaQuery } from 'hooks';
import Header from '../Header';
import NavbarToggle from 'components/NavbarToggle';
import breakpoints from 'utils/breakpoints';
import PropTypes from 'prop-types';
import countries from 'utils/countries.json';
import { ReactComponent as JavelinLogo } from 'assets/icons/logo-javelin-nav-top.svg';
import { useSelector } from 'react-redux';

export const NonFundraiserConfirmationScreen = (props) => {
  const {
    mobileSummary,
    desktopSummary,
    orderId,
    orderDetail,
    store
  } = props;

  const isLarge = useMediaQuery(breakpoints.lg);

  const orderCountry = useMemo(() => {
    return countries.find(({ code }) => code === orderDetail.country)?.code || ''
  }, [orderDetail.country]);

  const { terminalShippingMethods } = useSelector((state) => state.javelinStore);

  const ellipsisLoader = useMemo(() => (
    <span className="inline-block animate-ellipsis overflow-hidden align-bottom">...</span>
  ), []);

  const shippingAddress = useMemo(() => {
    return (
      <span className='grid grid-cols-2 gap-1 ml-auto mt-3' >
        <span className='font-medium' >Company Name:</span>
        <span className='font-normal' >{orderDetail.recipient_name}</span>
        <hr className='horizontal-line col-span-2 my-0' />
        <span className='font-medium' >Name:</span>
        <span className='font-normal' >{orderDetail?.shopper_name} {orderDetail?.shopper_surname}</span>
        <hr className='horizontal-line col-span-2 my-0' />
        <span className='font-medium' >Email:</span>
        <span className='font-normal' >{orderDetail.shopper_email}</span>
        <hr className='horizontal-line col-span-2 my-0' />
        <span className='font-medium' >Phone Number:</span>
        <span className='font-normal' >{orderDetail.recipient_phone_number}</span>
        <hr className='horizontal-line col-span-2 my-0' />
        <span className='font-medium' >Street Address:</span>
        <span className='font-normal' >{orderDetail.address_1} {orderDetail.address_2 ? `, ${orderDetail.address_2}` : ''}</span>
        <hr className='horizontal-line col-span-2 my-0' />
        <span className='font-medium' >City, State, Country:</span>
        <span className='font-normal' >
          {orderDetail.city}
          {orderDetail.state ? `, ${orderDetail.state}` : ''}
          {orderCountry ? `, ${orderCountry}` : ''}
        </span>
      </span>
    )
  }, [orderDetail, orderCountry]);

  const shippingMethod = useMemo(() => {
    if (!orderDetail.shipping_method.match(/^thsm_.*$/g)) {
      return orderDetail.shipping_method.replace('_', ' ');
    }

    return terminalShippingMethods.find(({ id }) => id === orderDetail.shipping_method)?.name;
  }, [terminalShippingMethods, orderDetail]);

  const desktopScreen = useMemo(() => {
    return (
      <div className="block lg:grid lg:grid-cols-12">
        <div className="lg:col-span-6 lg:min-h-screen lg:flex-1 lg:border lg:border-javelin-gray-border lg:bg-white lg:px-5 lg:pb-8 lg:pt-20 xl:col-span-7 xl:pl-36">
          <Header title={store?.data?.name} />
          <div data-testid="checkout-details" className="lg:mt-20 lg:pl-0">
            <p className="mb-2 text-lg font-bold text-javelin-gray-dark">
              {TEXT.header1(true)}
            </p>
            <p
              data-testid="order_id"
              className="my-7 mb-0 mt-3 text-sm font-bold text-javelin-gray-dark text-opacity-80"
            >
              {orderId}
            </p>
            <p
              data-testid="order_id"
              className="my-7 mb-0 mt-3 text-sm font-bold capitalize text-javelin-gray-dark text-opacity-80"
            >
              Status: {orderDetail.status}
            </p>
            {orderDetail.shipment_carrier && (
              <p
                data-testid="order_id"
                className="my-7 mb-0 mt-3 text-sm font-bold text-javelin-gray-dark text-opacity-80"
              >
                Shipment Provider: {orderDetail.shipment_carrier}
              </p>
            )}
            {orderDetail.tracking_id && (
              <p
                data-testid="order_id"
                className="my-7 mb-0 mt-3 text-sm font-bold text-javelin-gray-dark text-opacity-80"
              >
                Tracking Number: {orderDetail.tracking_id}
              </p>
            )}
            <hr className="horizontal-line my-4" />
            <div
              data-testid="order_id"
              className="my-7 mb-0 mt-3 flex-col items-center text-sm font-bold text-javelin-gray-dark text-opacity-80"
            >
              Shipping Address: {shippingAddress}
            </div>
            <hr className="horizontal-line my-4" />
            <p
              data-testid="order_id"
              className="my-7 mb-0 mt-3 text-sm font-bold capitalize text-javelin-gray-dark text-opacity-80"
            >
              Shipping Type: { shippingMethod || ellipsisLoader}
            </p>
          </div>
        </div>
        {desktopSummary}
      </div>
    );
  }, [desktopSummary, orderId, orderDetail, shippingAddress, store, shippingMethod, ellipsisLoader]);

  const mobileScreen = useMemo(() => {
    return (
      <>
        <NavbarToggle hasHamburgerIcon={false} LogoIcon={JavelinLogo} />
        <div className="flex-1 min-h-screen box-border w-full py-24 bg-javelin-background">
          <div className="flex flex-col items-start mt-2 mx-5 mb-5">
            <p className="text-lg text-javelin-gray-dark font-bold mb-2">
              {TEXT.header1(true)}
            </p>
            <p
              data-testid="order_id"
              className="text-sm font-bold text-opacity-80 mb-0 mt-3 text-javelin-gray-dark my-7"
            >
              {orderId}
            </p>
            <p
              data-testid="order_id"
              className="text-sm font-bold text-opacity-80 mb-0 capitalize mt-3 text-javelin-gray-dark my-7"
            >
              Status: {orderDetail.status}
            </p>
            {orderDetail.shipment_carrier && (
              <p
                data-testid="order_id"
                className="text-sm font-bold text-opacity-80 mb-0 mt-3 text-javelin-gray-dark my-7"
              >
                Shipment Provider: {orderDetail.shipment_carrier}
              </p>
            )}
            {orderDetail.tracking_id && (
              <p
                data-testid="order_id"
                className="text-sm font-bold text-opacity-80 mb-0 mt-3 text-javelin-gray-dark my-7"
              >
                Tracking Number: {orderDetail.tracking_id}
              </p>
            )}
            <hr className='horizontal-line my-4' />
            <div
              data-testid="order_id"
              className="text-sm font-bold w-full flex-col items-center text-opacity-80 mb-0 mt-0 text-javelin-gray-dark my-7"
            >
              Shipping Address: {shippingAddress}
            </div>
            <hr className='horizontal-line my-4' />
            <p
              data-testid="order_id"
              className="text-sm font-bold capitalize text-opacity-80 mb-0 mt-0 text-javelin-gray-dark my-7"
            >
              Shipping Type: {shippingMethod || ellipsisLoader}
            </p>
          </div>
          {mobileSummary}
        </div>
      </>
    )
  }, [mobileSummary, orderId, orderDetail, shippingAddress, shippingMethod, ellipsisLoader]);

  return isLarge ? desktopScreen : mobileScreen;
}

NonFundraiserConfirmationScreen.propTypes = {
  mobileSummary: PropTypes.element,
  desktopSummary: PropTypes.element,
  orderId: PropTypes.string,
  orderDetail: PropTypes.object,
  store: PropTypes.object
}
